// Progressive enhancement: every section remains visible without animation support.
(() => {
  const page = document.querySelector('.solvia-home');
  if (!page || page.dataset.motionReady) return;
  page.dataset.motionReady = 'true';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const canAnimate = typeof Element.prototype.animate === 'function';
  const activeAnimations = new Set();
  const initialized = new WeakSet();
  const revealed = new WeakSet();
  let frame = 0;

  const animate = (element, keyframes, options) => {
    if (reducedMotion.matches || !canAnimate) return null;
    const animation = element.animate(keyframes, options);
    activeAnimations.add(animation);
    const release = () => activeAnimations.delete(animation);
    animation.addEventListener('finish', release, { once: true });
    animation.addEventListener('cancel', release, { once: true });
    return animation;
  };

  const reveal = (element, delay = 0) => {
    if (revealed.has(element)) return;
    revealed.add(element);
    element.classList.add('solvia-motion-entered');
    animate(element, [
      { opacity: 0, transform: 'translate3d(0, 26px, 0)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    ], { duration: 760, delay, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'backwards' });
  };

  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      // Deep links and fast scrolling should never leave the destination waiting.
      const delay = target.getBoundingClientRect().top < 0 ? 0 : Number(target.dataset.motionDelay || 0);
      reveal(target, delay);
      revealObserver.unobserve(target);
    });
  }, { rootMargin: '0px 0px -24px 0px', threshold: 0 }) : null;

  function updateScroll() {
    frame = 0;
    if (reducedMotion.matches || document.hidden) return;
    const height = document.documentElement.scrollHeight - window.innerHeight;
    page.style.setProperty('--solvia-scroll-progress', height > 0 ? Math.min(1, Math.max(0, window.scrollY / height)) : 0);
  }

  function requestFrame() {
    if (!frame && !reducedMotion.matches && !document.hidden) frame = window.requestAnimationFrame(updateScroll);
  }

  const initialize = (root = document) => {
    const groups = [
      ['.solvia-hero__heading > *', 120],
      ['.solvia-hero__content > *', 110],
      ['.solvia-values__item', 100],
      ['.solvia-everyday__intro > *', 90],
      ['.solvia-demo__story', 100],
      ['.solvia-reviews__heading, .solvia-reviews__slider', 100],
      ['.solvia-first-steps__intro > *', 100],
      ['.solvia-first-steps__step', 140],
      ['.solvia-confidence__list > *', 80],
      ['.solvia-section-heading', 0],
      ['.solvia-showcase__grid > *', 130],
      ['.solvia-faq__intro, .solvia-faq__list', 120],
      ['.solvia-discover__main > h2', 0],
      ['.solvia-discover__choice', 120],
      ['.newsletter__wrapper > *', 100],
    ];
    groups.forEach(([selector, stagger]) => {
      // Reset the cadence for each section, including sections added in the theme editor.
      const parentCounts = new Map();
      root.querySelectorAll(selector).forEach((element) => {
        if (initialized.has(element)) return;
        initialized.add(element);
        const index = parentCounts.get(element.parentElement) || 0;
        parentCounts.set(element.parentElement, index + 1);
        element.dataset.motionDelay = Math.min(index * stagger, 300);
        if (revealObserver) revealObserver.observe(element);
        else reveal(element);
      });
    });
    // The cinematic controller gives the final photograph a gentle scroll drift.
    root.querySelectorAll('.solvia-discover__choice').forEach((card) => {
      if (card.dataset.motionPointer) return;
      card.dataset.motionPointer = 'true';
      let pointerFrame = 0;
      let pointerX = 0;
      let pointerY = 0;
      card.addEventListener('pointermove', (event) => {
        if (!finePointer.matches || reducedMotion.matches) return;
        pointerX = event.clientX;
        pointerY = event.clientY;
        if (pointerFrame) return;
        pointerFrame = window.requestAnimationFrame(() => {
          const bounds = card.getBoundingClientRect();
          card.style.setProperty('--solvia-pointer-x', `${pointerX - bounds.left}px`);
          card.style.setProperty('--solvia-pointer-y', `${pointerY - bounds.top}px`);
          pointerFrame = 0;
        });
      });
    });
    requestFrame();
  };

  // Keep native <details> semantics and keyboard behavior while easing its height.
  const disclosures = new WeakMap();
  const finishDisclosures = new Set();
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    if (!details?.matches('.solvia-faq__item')) return;
    if (event.defaultPrevented || reducedMotion.matches || !canAnimate) return;
    event.preventDefault();
    const previous = disclosures.get(details);
    const opening = previous ? !previous.opening : !details.open;
    const startHash = window.location.hash;
    const startHeight = details.getBoundingClientRect().height;
    previous?.animation.cancel();
    if (previous) finishDisclosures.delete(previous.finish);
    details.open = true;
    const endHeight = opening ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + details.offsetHeight - details.clientHeight;
    details.style.overflow = 'hidden';
    const animation = animate(details, [{ height: `${startHeight}px` }, { height: `${endHeight}px` }], {
      duration: opening ? 360 : 260, easing: 'cubic-bezier(.22, 1, .36, 1)',
    });
    const finish = () => {
      // A hash link may have opened the same answer while it was closing.
      const linked = details.id && window.location.hash !== startHash && window.location.hash === `#${details.id}`;
      details.open = opening || Boolean(linked);
      details.style.removeProperty('overflow');
      disclosures.delete(details);
      finishDisclosures.delete(finish);
      requestFrame();
    };
    disclosures.set(details, { animation, opening, finish });
    finishDisclosures.add(finish);
    animation.addEventListener('finish', finish, { once: true });
  });

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      activeAnimations.forEach((animation) => animation.cancel());
      finishDisclosures.forEach((finish) => finish());
    } else requestFrame();
  });
  document.addEventListener('visibilitychange', () => {
    activeAnimations.forEach((animation) => {
      if (document.hidden) animation.pause();
      else animation.play();
    });
    requestFrame();
  });
  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestFrame, { passive: true });
  window.addEventListener('pageshow', requestFrame);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-motion-delay]').forEach((element) => revealObserver?.unobserve(element));
  });
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll('[data-motion-delay]').forEach((element) => reveal(element));
  });
  initialize();
})();
